"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styles from "../css/inventoryItemDetail.module.css";
import Navbar from "../../../components/navbar";
import Footer from "../../../components/footer";
import Button from "../../../components/Button";
import { IcdModal } from "../components/IcdModal";
import { ExpiryModal } from "../components/ExpiryModal";
import { FrontShopLayout } from "../components/FrontShopLayout";
import { ClinicLayout } from "../components/ClinicLayout";
import { DispensaryLayout } from "../components/DispensaryLayout";
import { fetchInventoryItem, fetchSuppliers, updateInventoryItem } from "../../../api/Inventory/ProductCatalogueApi.js";
import SuccessNotification from "../../../components/SuccessNotification.jsx";
import { useAuth } from "../../../context/AuthContext";
import { getUserByEmail } from "../../../api/userManagement/userManagementApi";

export default function InventoryItemDetail() {
  const { itemId } = useParams(); // matches route path "/inventory/item/:itemId"
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
  const formatDateDMY = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [suppliersList, setSuppliersList] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierValue, setSupplierValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [branchCode, setBranchCode] = useState("");

  // Editable form state
  const [supplierCode, setSupplierCode] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [note, setNote] = useState("");
  const [binValue, setBinValue] = useState("");
  const [shelfValue, setShelfValue] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const d = await fetchInventoryItem(itemId);
        if (cancelled) return;
        setApiData(d);
      } catch (e) {
        if (cancelled) return;
        setError("Failed to load item");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchSuppliers();
        if (cancelled) return;
        const arr = Array.isArray(list) ? list : [];
        setSuppliersList(arr);
        const names = arr.map((s) => s.suppliername).filter(Boolean);
        setSupplierOptions(names);
        if (apiData?.suppliercode) {
          const match = arr.find((s) => String(s.suppliercode) === String(apiData.suppliercode));
          if (match?.suppliername) setSupplierValue(match.suppliername);
        } else if (apiData?.suppliername) {
          setSupplierValue(apiData.suppliername);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [apiData?.suppliercode]);

  useEffect(() => {
    if (!apiData) return;
    const drug = apiData.drug || {};
    const item = apiData.item || {};
    const packsizeSource = apiData.packsize; // string value from API (INVENTORY.PACKSIZE)

    setSupplierCode(apiData.suppliercode || "");
    setInvoiceNumber(apiData.invoicenumber || "");
    setCostPrice(apiData.costprice ?? "");
    setNote(apiData.note || "");
    setQty(Number(apiData.quantity ?? 0));
    setReorder(Number(apiData.reorderlevel ?? 0));
    setPackSize(packsizeSource ?? "");
    setBinValue(drug.binnumber || "");
    setShelfValue(item.shelvenumber || "");
  }, [apiData]);

  const data = useMemo(() => {
    const d = apiData || {};
    const drug = d.drug || {};
    const item = d.item || {};
    const params = new URLSearchParams(location.search);
    const fromFrontShopFlag = params.get("fromFrontShop") === "1";
    const storageParam = (params.get("storage") || "").toLowerCase();
    // Any item opened as Dispensing storage from the Front Shop tab should hide invoice number
    const viewedAsFrontShopDrug = fromFrontShopFlag && storageParam.includes("dispensary");
    return {
      title: d.name || "",
      sku: d.sku || itemId,
      ingredient: drug.activeingredient || "",
      strength: drug.strength || "",
      nappi: drug.nappicode || d.barcode || "",
      barcode: d.barcode || "",
      schedule: drug.schedule || "",
      dosageForm: drug.dosageform || "",
      icd10: drug.icd10 || "",
      binNumber: drug.binnumber || item.shelvenumber || "",
      quantity: Number(d.quantity ?? 0),
      reorderLevel: Number(d.reorderlevel ?? 0),
      packSize: d.packsize ?? "",
      expiry: d.expiry || "",
      expiryDates: Array.isArray(d.expiryDates) ? d.expiryDates : [],
      costPrice: d.costprice ?? "",
      supplier: d.suppliername || "",
      supplierCode: d.suppliercode || "",
      invoiceNumber: viewedAsFrontShopDrug ? "" : (d.invoicenumber ?? ""),
      hideInvoiceNumber: viewedAsFrontShopDrug,
      lastReorder: d.lastReorder || "",
      note: d.note ?? "",
      categoryFromApi: d.category ?? d.categoryName ?? item.category ?? "",
    };
  }, [apiData, itemId, location.search]);

  const skuDisplay = useMemo(() => {
    const rawSku = data?.sku || "";
    const bc = (branchCode || user?.branchcode) ? String(branchCode || user?.branchcode).trim() : "";
    return bc ? `${bc} - ${rawSku}` : rawSku;
  }, [data?.sku, user?.branchcode, branchCode]);

  // Debug: trace SKU and branch code used for display
  console.debug('[InventoryItemDetail] skuDisplay:', skuDisplay, 'rawSku:', data?.sku, 'branchCode:', branchCode || user?.branchcode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user) return;
        const hasBranch = user?.branchcode && String(user.branchcode).trim();
        const hasLocal = branchCode && String(branchCode).trim();
        if (hasBranch || hasLocal) return;

        // Try parse token payload for branchcode/email
        let tokenEmail = null;
        let tokenBranch = null;
        try {
          const storedToken = localStorage.getItem("token");
          if (storedToken && storedToken.split(".").length === 3) {
            const payload = JSON.parse(atob(storedToken.split(".")[1]));
            tokenEmail = payload?.email || payload?.username || payload?.sub || null;
            tokenBranch = payload?.branchcode || payload?.branch || null;
          }
        } catch {}

        if (tokenBranch && String(tokenBranch).trim()) {
          if (!cancelled) setBranchCode(String(tokenBranch));
          console.debug('[InventoryItemDetail] Resolved branchcode from token payload:', tokenBranch);
          return;
        }

        // Prefer a real email; skip non-email identifiers like EMP004
        const email = (user?.email || tokenEmail || user?.username);
        if (!email || !String(email).includes('@')) {
          console.debug('[InventoryItemDetail] Skipping user API lookup; no valid email identifier. user.email:', user?.email, 'tokenEmail:', tokenEmail);
          return;
        }
        const u = await getUserByEmail(String(email));
        if (cancelled) return;
        const bc = u?.branchcode || (Array.isArray(u) ? u[0]?.branchcode : "");
        if (bc) setBranchCode(String(bc));
        console.debug('[InventoryItemDetail] Resolved branchcode from user API:', bc, 'for email identifier:', email);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [user, branchCode]);

  const params = new URLSearchParams(location.search);
  const storageParam = (params.get("storage") || "").toLowerCase();
  const isFrontShop = (apiData?.type === 'item' || (!!apiData?.item && !apiData?.drug)) || storageParam.includes("front shop");
  const isClinic = storageParam
    ? storageParam.includes("clinic")
    : false;
  const isOtc = storageParam
    ? storageParam.includes("otc")
    : false;

  // Determine if there is meaningful drug info
  const hasDrugInfo = useMemo(() => {
    const drug = apiData?.drug || {};
    const icdRaw = Array.isArray(drug.icd10) ? drug.icd10 : (drug.icd10 ? String(drug.icd10).trim() : "");
    const hasIcd = Array.isArray(icdRaw) ? icdRaw.filter(Boolean).length > 0 : icdRaw.length > 0;
    return Boolean(
      (drug.activeingredient && String(drug.activeingredient).trim()) ||
      (drug.strength && String(drug.strength).trim()) ||
      (drug.schedule != null && String(drug.schedule).trim()) ||
      (drug.dosageform && String(drug.dosageform).trim()) ||
      hasIcd
    );
  }, [apiData]);

  // Tabs state derived from storage or binNumber
  const initialTab = useMemo(() => {
    if (isFrontShop) return "Front Shop";
    if (isClinic) return "Clinic";
    if (isOtc) return "OTC";
    // default
    return "Dispensary";
  }, [isFrontShop, isClinic, isOtc]);
  const [activeTab, setActiveTab] = useState(initialTab);
  useEffect(() => setActiveTab(initialTab), [initialTab]);

  // ICD-10 state and helpers
  const initialIcdList = useMemo(() => {
    const raw = String(data?.icd10 || "");
    const parts = raw
      .split(/[\s/,+;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return parts.length ? parts : [raw];
  }, [data]);

  // Expiry state and helpers (similar to ICD-10)
  const initialExpiryList = useMemo(() => {
    const arr = Array.isArray(data?.expiryDates) ? data.expiryDates : [];
    const parts = arr
      .map((d) => (d instanceof Date ? d.toISOString().slice(0,10) : (new Date(d)).toISOString().slice(0,10)))
      .filter((s) => !!s && s !== "Invalid Date");
    return parts.length ? parts : [String(data?.expiry || "-")];
  }, [data]);

  const [expiryList, setExpiryList] = useState(initialExpiryList);
  const [showExpiryModal, setShowExpiryModal] = useState(false);

  useEffect(() => {
    setExpiryList(initialExpiryList);
  }, [initialExpiryList]);

  const cleanedExpiryList = expiryList.filter(Boolean);
  const expiryDisplay = formatDateDMY(cleanedExpiryList[0] || String(data?.expiry || "-"));
  const hasMultipleExpiry = cleanedExpiryList.length > 1;
  const addExpiry = () => setExpiryList((prev) => [...prev, ""]);
  const removeExpiryAt = (idx) => setExpiryList((prev) => prev.filter((_, i) => i !== idx));
  const updateExpiryAt = (idx, val) => setExpiryList((prev) => prev.map((v, i) => (i === idx ? val : v)));
  const decExpiryCount = () => setExpiryList((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  const incExpiryCount = () => addExpiry();

  const [icdList, setIcdList] = useState(initialIcdList);
  const [showIcdModal, setShowIcdModal] = useState(false);

  // keep icd list in sync if item changes
  useEffect(() => {
    setIcdList(initialIcdList);
  }, [initialIcdList]);

  const icdDisplay = (icdList.filter(Boolean).join(" / ")) || String(data?.icd10 || "-");

  const addIcd = () => setIcdList((prev) => [...prev, ""]);
  const removeIcdAt = (idx) =>
    setIcdList((prev) => prev.filter((_, i) => i !== idx));
  const updateIcdAt = (idx, val) =>
    setIcdList((prev) => prev.map((v, i) => (i === idx ? val.toUpperCase() : v)));

  const decrementCount = () =>
    setIcdList((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  const incrementCount = () => addIcd();

  // Numeric fields state (spinner values) and pack size (string from INVENTORY.PACKSIZE)
  const [qty, setQty] = useState(Number(data.quantity) || 0);
  const [reorder, setReorder] = useState(Number(data.reorderLevel) || 0);
  const [packSize, setPackSize] = useState(data.packSize || "");
  // Category state and options
  const categoryOptions = useMemo(
    () => [
      "General",
      "Antibiotics",
      "Analgesics",
      "Antihistamines",
      "Diabetes",
      "Injection",
      "Skincare",
      "Vitamins",
      "OTC",
      "Other"
    ],
    []
  );
  const inferCategory = useMemo(() => {
    const explicit = String(data?.categoryFromApi || "").trim();
    if (explicit) return explicit;
    const t = String(data?.title || "").toLowerCase();
    const form = String(data?.dosageForm || "").toLowerCase();
    if (t.includes("amox") || t.includes("insulin")) return "Antibiotics";
    if (t.includes("panadol") || t.includes("motrin") || form.includes("tablet")) return "Analgesics";
    if (t.includes("claritin") || t.includes("lorat")) return "Antihistamines";
    if (t.includes("nivea") || form.includes("lotion")) return "Skincare";
    if (form.includes("injection")) return "Injection";
    return "General";
  }, [data]);
  const [category, setCategory] = useState(inferCategory);
  useEffect(() => setCategory(inferCategory), [inferCategory]);
  const [customCategory, setCustomCategory] = useState("");
  useEffect(() => {
    setQty(Number(data.quantity) || 0);
    setReorder(Number(data.reorderLevel) || 0);
    setPackSize(data.packSize || "");
  }, [data]);

  // Live validation
  const errors = useMemo(() => {
    const e = {};
    const cp = Number(costPrice);
    if (costPrice !== "" && (!Number.isFinite(cp) || cp < 0)) e.costPrice = "Cost must be a non-negative number";
    if (!Number.isFinite(Number(qty)) || Number(qty) < 0) e.qty = "Quantity must be a non-negative number";
    if (!Number.isFinite(Number(reorder)) || Number(reorder) < 0) e.reorder = "Reorder must be a non-negative number";
    if (invoiceNumber.length > 64) e.invoiceNumber = "Too long";
    if (note.length > 1000) e.note = "Too long";
    return e;
  }, [costPrice, qty, reorder, packSize, invoiceNumber, note]);
  const isValid = Object.keys(errors).length === 0;
  const isDirty = useMemo(() => {
    if (!apiData) return false;
    const baselineCategory =
      apiData?.category ?? apiData?.categoryName ?? apiData?.item?.category ?? "";
    const selectedCategory = category === "Other" ? (customCategory || "") : category;
    const baselineBin = apiData?.drug?.binnumber || "";
    const baselineShelf = apiData?.item?.shelvenumber || "";
    return (
      Number(apiData.quantity ?? 0) !== Number(qty) ||
      Number(apiData.reorderlevel ?? 0) !== Number(reorder) ||
      String(apiData.packsize ?? "") !== String(packSize) ||
      String(apiData.costprice ?? "") !== String(costPrice) ||
      String(apiData.suppliercode ?? "") !== String(supplierCode) ||
      String(apiData.invoicenumber ?? "") !== String(invoiceNumber) ||
      String(apiData.note ?? "") !== String(note) ||
      String(baselineBin) !== String(binValue) ||
      String(baselineShelf) !== String(shelfValue) ||
      String(baselineCategory) !== String(selectedCategory)
    );
  }, [apiData, qty, reorder, packSize, costPrice, supplierCode, invoiceNumber, note, binValue, shelfValue, category, customCategory]);

  // Global read-only mode for this view
  const readOnly = true;

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
            <div className={styles.card}>
              {showSuccess && (
                <SuccessNotification
                  message={`${data.title || "Item"} updated`}
                  onClose={() => setShowSuccess(false)}
                />
              )}
              {loading ? (
                <h1 className={styles.title}>Loading...</h1>
              ) : error ? (
                <h1 className={styles.title}>{error}</h1>
              ) : (
                <h1 className={styles.title}>{data.title}</h1>
              )}

              {activeTab === "Front Shop" ? (
                <FrontShopLayout
                  data={{
                    ...data,
                    sku: skuDisplay,
                    supplierCode,
                    invoiceNumber,
                    costPrice,
                    note,
                  }}
                  qty={qty}
                  setQty={setQty}
                  reorder={reorder}
                  setReorder={setReorder}
                  packSize={packSize}
                  setPackSize={setPackSize}
                  expiryDisplay={expiryDisplay}
                  hasMultipleExpiry={hasMultipleExpiry}
                  setShowExpiryModal={setShowExpiryModal}
                  category={category}
                  setCategory={setCategory}
                  customCategory={customCategory}
                  setCustomCategory={setCustomCategory}
                  categoryOptions={categoryOptions}
                  supplierOptions={supplierOptions}
                  supplierValue={supplierValue}
                  onChangeSupplierName={(name) => {
                    setSupplierValue(name);
                    const match = suppliersList.find((s) => s.suppliername === name);
                    if (match?.suppliercode) setSupplierCode(String(match.suppliercode));
                  }}
                  onChangeSupplierCode={setSupplierCode}
                  onChangeInvoiceNumber={setInvoiceNumber}
                  onChangeCostPrice={setCostPrice}
                  onChangeNote={setNote}
                  shelfValue={shelfValue}
                  onChangeShelfNumber={setShelfValue}
                  readOnly={readOnly}
                />
              ) : activeTab === "Clinic" ? (
                <ClinicLayout
                  data={{ ...data, sku: skuDisplay }}
                  qty={qty}
                  setQty={setQty}
                  reorder={reorder}
                  setReorder={setReorder}
                  packSize={packSize}
                  setPackSize={setPackSize}
                  icdDisplay={icdDisplay}
                  setShowIcdModal={(v) => {
                    if (!readOnly) setShowIcdModal(v);
                  }}
                  expiryDisplay={expiryDisplay}
                  hasMultipleExpiry={hasMultipleExpiry}
                  setShowExpiryModal={setShowExpiryModal}
                  category={category}
                  setCategory={setCategory}
                  customCategory={customCategory}
                  setCustomCategory={setCustomCategory}
                  categoryOptions={categoryOptions}
                  hideDrugFields={isFrontShop}
                  readOnly={readOnly}
                />
              ) : (
                <DispensaryLayout
                  data={{
                    ...data,
                    sku: skuDisplay,
                    invoiceNumber,
                    costPrice,
                    note,
                  }}
                  qty={qty}
                  setQty={setQty}
                  reorder={reorder}
                  setReorder={setReorder}
                  packSize={packSize}
                  setPackSize={setPackSize}
                  icdDisplay={icdDisplay}
                  setShowIcdModal={(v) => {
                    if (!readOnly) setShowIcdModal(v);
                  }}
                  expiryDisplay={expiryDisplay}
                  hasMultipleExpiry={hasMultipleExpiry}
                  setShowExpiryModal={setShowExpiryModal}
                  category={category}
                  setCategory={setCategory}
                  customCategory={customCategory}
                  setCustomCategory={setCustomCategory}
                  categoryOptions={categoryOptions}
                  binValue={binValue}
                  onChangeBinNumber={setBinValue}
                  onChangeInvoiceNumber={setInvoiceNumber}
                  onChangeCostPrice={setCostPrice}
                  onChangeNote={setNote}
                  readOnly={readOnly}
                />
              )}

              <hr className={styles.divider} />
              <div className={styles.actions}>
                {!readOnly && (
                  <Button
                    variant="primary"
                    disabled={!isDirty || saving}
                    onClick={async () => {
                      try {
                        setSaving(true);
                        const payload = {
                          quantity: Number(qty),
                          reorderlevel: Number(reorder),
                          packsize: Number(packSize),
                          costprice: costPrice === "" ? null : Number(costPrice),
                          suppliercode: supplierCode,
                          invoicenumber: invoiceNumber,
                          note,
                        };
                        // Optional relational updates
                        const selectedCategory =
                          category === "Other" ? customCategory || "" : category;
                        if (String(apiData?.drug?.binnumber || "") !== String(binValue)) {
                          payload.binnumber = binValue;
                        }
                        if (String(apiData?.item?.shelvenumber || "") !== String(shelfValue)) {
                          payload.shelvenumber = shelfValue;
                        }
                        if (
                          selectedCategory &&
                          String(apiData?.item?.category || "") !== String(selectedCategory)
                        ) {
                          payload.category = selectedCategory;
                        }
                        await updateInventoryItem(itemId, payload);
                        setShowSuccess(true);
                        // Sync apiData baseline
                        setApiData((prev) =>
                          prev
                            ? {
                                ...prev,
                                quantity: payload.quantity,
                                reorderlevel: payload.reorderlevel,
                                packsize: payload.packsize,
                                costprice: payload.costprice,
                                suppliercode: payload.suppliercode,
                                invoicenumber: payload.invoicenumber,
                                note: payload.note,
                                drug: {
                                  ...(prev.drug || {}),
                                  binnumber:
                                    payload.binnumber != null
                                      ? payload.binnumber
                                      : prev.drug?.binnumber,
                                },
                                item: {
                                  ...(prev.item || {}),
                                  shelvenumber:
                                    payload.shelvenumber != null
                                      ? payload.shelvenumber
                                      : prev.item?.shelvenumber,
                                  category:
                                    payload.category != null
                                      ? payload.category
                                      : prev.item?.category,
                                },
                              }
                            : prev
                        );
                      } catch (e) {
                        alert("Failed to save changes");
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>
                )}
              </div>
            </div>
            <IcdModal
              showIcdModal={showIcdModal}
              setShowIcdModal={setShowIcdModal}
              icdList={icdList}
              decrementCount={decrementCount}
              incrementCount={incrementCount}
              updateIcdAt={updateIcdAt}
              removeIcdAt={removeIcdAt}
            />
            <ExpiryModal
              showExpiryModal={showExpiryModal}
              setShowExpiryModal={setShowExpiryModal}
              expiryList={expiryList}
              decExpiryCount={decExpiryCount}
              incExpiryCount={incExpiryCount}
              updateExpiryAt={updateExpiryAt}
              removeExpiryAt={removeExpiryAt}
              readOnly={readOnly}
            />
          </div>
        </main>
      </div>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}
